/**
 * LE VOCABULAIRE DE LA SUCCESSION — un seul jeu de mots, lu de la base.
 *
 * ⚠️⚠️ ÉCRIT PARCE QUE C'EST LE MOTIF EXACT QUI A RENDU LA MAINLEVÉE
 * INDÉPOSABLE. Un `z.enum` de dix valeurs tapé au point d'entrée, alors que
 * cinq couches connaissaient déjà la liste : le jour où la sixième arrive, le
 * dépôt refuse une valeur que la base accepte, et le refus parle d'un champ que
 * l'agent n'a pas rempli. Les valeurs ci-dessous vivaient en TROIS exemplaires
 * — la contrainte en base, le `z.enum` de `modules/successions.ts`, et les
 * menus de l'écran — sans que rien ne les confronte.
 *
 * ⚠️ LA BASE FAIT FOI, ET `tests/vocabulaire-succession.test.ts` LA CONFRONTE
 * DANS LES DEUX SENS : une valeur proposée ici et refusée en base fait échouer
 * l'inscription au dernier moment ; une valeur admise en base et absente ici
 * n'est jamais proposée à personne. Les deux se voient au rouge, pas en
 * production.
 *
 * ⚠️ LES LIBELLÉS SONT SÉPARÉS DES CODES, et c'est la règle de ce dépôt : le
 * code est la valeur de la base, le libellé est ce que l'agent lit. Un menu qui
 * afficherait `legataire_a_titre_universel` parle le vocabulaire de la table,
 * pas celui du droit — c'est le défaut que le fondateur a signalé sur les types
 * de mutation (« le parcours cadastral est incompréhensible »).
 */

/**
 * LA QUALITÉ D'UN AYANT DROIT — `fon.qualite_successorale` (migration 158).
 *
 * ⚠️ HÉRITIER ET LÉGATAIRE NE SE CONFONDENT PAS, et les trois légataires non
 * plus. Le légataire UNIVERSEL a vocation à tout, celui À TITRE UNIVERSEL à une
 * quote-part, le PARTICULIER à un bien désigné — et seul le dernier échappe au
 * passif. Les replier sur « légataire » ferait supporter une dette à qui ne la
 * doit pas.
 */
export const QUALITES_SUCCESSORALES = [
  'conjoint_survivant', 'descendant', 'ascendant', 'collateral',
  'legataire_universel', 'legataire_a_titre_universel', 'legataire_particulier',
] as const;
export type QualiteSuccessorale = (typeof QUALITES_SUCCESSORALES)[number];

export const LIBELLE_QUALITE: Record<QualiteSuccessorale, string> = {
  conjoint_survivant: 'Conjoint survivant',
  descendant: 'Descendant (enfant, petit-enfant)',
  ascendant: 'Ascendant (père, mère)',
  collateral: 'Collatéral (frère, sœur, neveu)',
  legataire_universel: 'Légataire universel — vocation à tout',
  legataire_a_titre_universel: 'Légataire à titre universel — vocation à une quote-part',
  legataire_particulier: 'Légataire particulier — un bien désigné',
};

/**
 * COMMENT LE DÉCÈS EST ÉTABLI — `fon.succession.source` (contrainte
 * `succession_source_check`).
 *
 * ⚠️⚠️ AUCUNE VALEUR PAR DÉFAUT N'EST PROPOSÉE PAR L'ÉCRAN, et c'est une
 * décision. Pré-cocher « acte de décès » ferait porter au registre qu'un acte
 * existe quand personne ne l'a vu — et c'est sur cette mention qu'un tiers
 * s'appuiera dans dix ans pour dire que la mort était établie. Une déclaration
 * d'héritier et un acte d'état civil ne se plaident pas pareil.
 */
export const SOURCES_DECES = ['acte_deces', 'jugement', 'etat_civil', 'declaration'] as const;
export type SourceDeces = (typeof SOURCES_DECES)[number];

export const LIBELLE_SOURCE_DECES: Record<SourceDeces, string> = {
  acte_deces: "Acte de décès (état civil)",
  jugement: 'Jugement déclaratif de décès',
  etat_civil: "Extrait des registres d'état civil",
  declaration: "Déclaration des ayants droit — aucune pièce produite",
};

/**
 * D'OÙ VIENT LA VOCATION D'UN AYANT DROIT — `fon.ayant_droit.source`
 * (contrainte `ayant_droit_source_check`).
 *
 * ⚠️ CE N'EST PAS LA MÊME LISTE QUE `SOURCES_DECES`, et les confondre serait
 * une faute de droit, pas une commodité : établir qu'un homme est mort et
 * établir qui lui succède sont deux démonstrations distinctes. Un acte de décès
 * ne dit rien des enfants ; un acte de notoriété ne prouve pas la mort.
 */
export const SOURCES_DEVOLUTION = [
  'acte_notarie', 'jugement', 'etat_civil', 'testament', 'declaration',
] as const;
export type SourceDevolution = (typeof SOURCES_DEVOLUTION)[number];

export const LIBELLE_SOURCE_DEVOLUTION: Record<SourceDevolution, string> = {
  acte_notarie: "Acte de notoriété reçu par notaire",
  jugement: "Jugement d'hérédité",
  etat_civil: "Pièces d'état civil (livret de famille, actes de naissance)",
  testament: 'Testament',
  declaration: "Déclaration des ayants droit — aucune pièce produite",
};

/**
 * L'OPTION SUCCESSORALE — `fon.ayant_droit.option`.
 *
 * ⚠️ `indeterminee` EST LE DÉFAUT DE LA BASE, ET IL N'EST PAS UN OUBLI : le
 * silence ne vaut pas acceptation, sinon on ferait supporter le passif à qui ne
 * l'a pas voulu. Il n'est donc JAMAIS proposé comme un geste — on ne « choisit »
 * pas de ne pas opter, on ne l'a pas encore fait.
 */
export const OPTIONS_EXERCABLES = ['acceptee', 'acceptee_a_concurrence', 'renoncee'] as const;
export type OptionExercable = (typeof OPTIONS_EXERCABLES)[number];

export const LIBELLE_OPTION: Record<string, string> = {
  indeterminee: 'Non exercée',
  acceptee: 'Acceptée',
  acceptee_a_concurrence: "Acceptée à concurrence de l'actif net",
  renoncee: 'Renoncée',
};

/** `fon.masse_successorale.nature` — ce qui augmente, ce qui diminue. */
export const NATURES_MASSE = ['actif', 'passif'] as const;
export type NatureMasse = (typeof NATURES_MASSE)[number];

/** `fon.masse_successorale.categorie`. */
export const CATEGORIES_MASSE = [
  'immeuble', 'meuble', 'creance', 'liquidite', 'dette',
  'frais_funeraires', 'impot', 'autre',
] as const;
export type CategorieMasse = (typeof CATEGORIES_MASSE)[number];

export const LIBELLE_CATEGORIE_MASSE: Record<CategorieMasse, string> = {
  immeuble: 'Immeuble', meuble: 'Meuble', creance: 'Créance', liquidite: 'Liquidité',
  dette: 'Dette', frais_funeraires: 'Frais funéraires', impot: 'Impôt', autre: 'Autre',
};

/**
 * `fon.masse_successorale.source_valeur`.
 *
 * ⚠️ UNE VALEUR SANS SOURCE NE VAUT PAS ÉVALUATION — la route le dit déjà dans
 * son avertissement. La liste est ici pour que l'écran propose les cinq, et non
 * pour qu'il en invente une sixième.
 */
export const SOURCES_VALEUR = ['declaration', 'expertise', 'bareme', 'acte', 'marche'] as const;
export type SourceValeur = (typeof SOURCES_VALEUR)[number];

export const LIBELLE_SOURCE_VALEUR: Record<SourceValeur, string> = {
  declaration: "Déclaration de l'ayant droit",
  expertise: 'Expertise',
  bareme: 'Barème administratif',
  acte: 'Acte (vente, donation antérieure)',
  marche: 'Prix de marché constaté',
};
